import { css } from '@/styled-system/css';
import styled from 'styled-components';

const PHOTO_THUMB_WIDTH = 80;

export const photoGalleryContainer = css({
  display: 'grid',
  gridTemplateRows: 'auto 1fr auto auto',
  gridTemplateAreas: {
    base: '"header" "carousel" "info" "footer"',
  },
  height: 'calc(100dvh - 32px)',
  width: '100%',
  minHeight: 0,
  backgroundColor: 'black',
});

export const headerArea = css({
  gridArea: 'header',
  zIndex: 'base',
  position: 'relative',
  textAlign: 'center',
  fontSize: 'md',
  marginTop: '12px',
  marginBottom: '12px',
  marginX: '8px',
  md: {
    display: 'flex',
    margin: '20px',
  },
});

export const carouselArea = css({
  gridArea: 'carousel',
  margin: 'auto',
  position: 'relative',
  height: '100%',
  width: '100%',
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
});

export const infoArea = css({
  gridArea: 'info',
  fontSize: 'sm',
  width: '80%',
  textAlign: 'center',
  marginX: 'auto',
  color: 'white',
  textShadow: '1px 1px black',
});

export const infoAreaMobileModelFloorplan = css({
  gridArea: 'info',
  fontSize: 'sm',
  width: '80%',
  textAlign: 'center',
  marginX: 'auto',
  color: 'white',
  textShadow: '1px 1px black',
  paddingTop: '16px', // Add 16px spacing from carousel for mobile ModelFloorplanItem
});

export const footerArea = css({
  gridArea: 'footer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
});

export const contactButton = css({
  display: 'flex',
  paddingY: '8px',
  paddingX: {
    base: '20px', // Better interior padding for mobile
    md: '40px', // Original padding for desktop
  },
  alignItems: 'center',
  minWidth: {
    base: '160px', // Ensure adequate width for content
    md: 'auto', // Auto width on desktop
  },
  justifyContent: 'center', // Center the content within the button
  whiteSpace: 'nowrap', // Prevent text wrapping
});

export const contactIcon = css({
  marginRight: '8px',
  height: '16px',
  width: '16px',
});

export const infoLink = css({
  color: 'white',
  fontSize: {
    base: 'sm',
    md: 'md',
  },
});

export const imageCaption = css({
  color: 'white',
  textAlign: 'center',
  marginBottom: '8px',
});

export const photoNav = css({
  userSelect: 'none',
  width: '80%',
  maxWidth: '100%',
  marginTop: '0',
  marginBottom: '0',
  marginLeft: 'auto',
  marginRight: 'auto',
  overflowX: 'auto',
  overflowY: 'hidden',
  whiteSpace: 'nowrap',
  position: 'relative',
  scrollBehavior: 'smooth',
  display: {
    base: 'none',
    md: 'block',
  },
  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.1)',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    borderBottomRightRadius: '4px',
    borderBottomLeftRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.3)',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    borderBottomRightRadius: '4px',
    borderBottomLeftRadius: '4px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.5)',
    },
  },
  // Firefox scrollbar
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1)',
});

export const photoNavList = css({
  marginTop: 0,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  transitionProperty: 'transform',
  transitionDuration: '0.45s',
  transitionTimingFunction: 'ease-out',
  whiteSpace: 'nowrap',
  display: 'flex',
  willChange: 'transform',
});

export const photoThumbWrapper = css({
  position: 'relative',
  paddingTop: '10px',
  paddingBottom: '10px',
  paddingLeft: '10px',
  paddingRight: '10px',
  display: 'block',
  width: `${PHOTO_THUMB_WIDTH}px`,
  height: `${PHOTO_THUMB_WIDTH}px`,
  cursor: 'pointer',
  flexShrink: 0,
  boxSizing: 'border-box',
});

export const photoThumb = css({
  verticalAlign: 'middle',
  width: '100%',
  height: '100%',
});

export const overlay = css({
  width: '100%',
  height: '100%',
  position: 'relative',
  '&:after': {
    zIndex: 1,
    position: 'absolute',
    transitionProperty: 'all',
    transitionDuration: '0.25s',
    transitionTimingFunction: 'ease',
    content: '" "',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    opacity: 1,
  },
});

export const activeThumbWrapper = css({
  '& button:after': {
    opacity: 0,
  },
});

export const hoverThumbWrapper = css({
  _hover: {
    '& button:after': {
      opacity: 0,
    },
  },
});

export const floorplanButtonContainer = css({
  display: 'flex',
  width: '100%',
  justifyContent: 'center',
  padding: '0', // Remove all padding to rely on flex gap
});

export const floorplanButton = css({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
});

export const floorplanButtonCaret = css({
  width: '14px',
  height: '14px',
  marginLeft: '8px',
});

export const tours3dButtonTop = css({
  color: 'white',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'white',
  display: {
    base: 'none',
    md: 'inline-block',
  },
  paddingTop: '8px',
  paddingBottom: '8px',
  paddingLeft: '40px',
  paddingRight: '40px',
  marginLeft: '16px',
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px',
  borderBottomRightRadius: '4px',
  borderBottomLeftRadius: '4px',
  cursor: 'pointer',
});

export const tours3dButtonBottom = css({
  color: 'white',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'white',
  display: {
    base: 'block',
    md: 'none',
  },
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px',
  borderBottomRightRadius: '4px',
  borderBottomLeftRadius: '4px',
  paddingTop: '8px',
  paddingBottom: '8px',
  paddingLeft: '16px',
  paddingRight: '16px',
  fontWeight: 'bold',
  fontSize: 'sm',
  textAlign: 'center',
  width: '100%',
});

export const tours3dIcon = css({
  marginRight: '8px',
  height: '16px',
  width: '16px',
});

export const imageCache = css({
  '& img, & div': {
    display: 'none',
  },
});

export const photoImage = css({
  maxWidth: '100%',
  maxHeight: '100%',
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
});

export const StyledPhotoCountText = styled.span`
  color: white;
  fill: white;
  font-weight: bold;
  display: block;
`;

export const mobileHeaderLeft = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flex: 1,
});

export const mobileHeaderCenter = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
});

export const mobileHeaderRight = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flex: 1,
});

export const mobileHeader = css({
  display: {
    base: 'flex',
    md: 'none',
  },
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingY: '16px', // Update from 12px to 16px for consistent spacing
  paddingX: '16px',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
});

export const mobileFooter = css({
  display: {
    base: 'flex',
    md: 'none',
  },
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
  padding: '16px',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
});

export const desktopFooter = css({
  display: {
    base: 'none',
    md: 'flex',
  },
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
  padding: '16px',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  position: 'relative',
});

export const morePhotosIndicator = css({
  position: 'absolute',
  top: '16px', // Match desktopFooter padding
  right: '10%', // photoNav is 80% width, so right edge is at 10% from right
  height: '80px', // Match photoNav height (PHOTO_THUMB_WIDTH)
  width: '60px',
  background: 'linear-gradient(to right, transparent, rgba(0, 0, 0, 0.8))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
  borderTopLeftRadius: '0',
  borderTopRightRadius: '4px',
  borderBottomRightRadius: '4px',
  borderBottomLeftRadius: '0',
  transitionProperty: 'opacity',
  transitionDuration: '0.2s',
  transitionTimingFunction: 'ease-in-out',
});

export const morePhotosArrow = css({
  color: 'white',
  fontSize: '18px',
  fontWeight: 'bold',
  textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
});

export const morePhotosText = css({
  color: 'white',
  fontSize: 'xs',
  fontWeight: 'bold',
  textAlign: 'center',
  textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
  marginLeft: '4px',
});
